/**
 * IBD Cylindo viewer — lazy loader.
 *
 * Loads the Cylindo CDN modules and the theme wrapper module
 * (`@theme/ibd-cylindo-viewer`) only when a Cylindo viewer element actually
 * exists in the live DOM. PDP zoom keeps the viewer in a `<template>` until
 * `ZoomDialog.open()` stamps a clone into the host, so this watcher will not
 * see `cylindo-viewer` on first HTML. The watcher stays global because
 * quick-add can insert a Cylindo product gallery on any page.
 *
 * Defining the custom elements upgrades any matching elements already in the
 * DOM, so markup stamped before the modules load works unchanged.
 */

const CYLINDO_ELEMENTS = 'cylindo-viewer, ibd-cylindo-viewer, ibd-cylindo-slide-anchor';

const CYLINDO_CDN_MODULES = [
  'https://viewer-cdn.cylindo.com/v1/index.mjs',
  'https://viewer-cdn.cylindo.com/v1/bar.mjs',
];

/** Cylindo serves the viewer's frame images from this origin. */
const CYLINDO_CONTENT_ORIGIN = 'https://content.cylindo.com';

let loadRequested = false;

function loadCylindo() {
  if (loadRequested) return;
  loadRequested = true;
  observer.disconnect();

  const preconnect = document.createElement('link');
  preconnect.rel = 'preconnect';
  preconnect.href = CYLINDO_CONTENT_ORIGIN;
  preconnect.crossOrigin = '';
  document.head.appendChild(preconnect);

  for (const src of CYLINDO_CDN_MODULES) {
    if (document.querySelector(`script[src="${src}"]`)) continue;
    const script = document.createElement('script');
    script.type = 'module';
    script.src = src;
    document.head.appendChild(script);
  }

  void import('@theme/ibd-cylindo-viewer');
}

/**
 * @param {ParentNode} [root]
 */
function scan(root = document) {
  if (root.querySelector(CYLINDO_ELEMENTS)) loadCylindo();
}

const observer = new MutationObserver((mutations) => {
  for (const mutation of mutations) {
    for (const node of mutation.addedNodes) {
      if (!(node instanceof Element)) continue;
      if (node.matches(CYLINDO_ELEMENTS) || node.querySelector(CYLINDO_ELEMENTS)) {
        loadCylindo();
        return;
      }
    }
  }
});
observer.observe(document.documentElement, { childList: true, subtree: true });

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => scan(), { once: true });
} else {
  scan();
}
